import { PITCH_CONFIDENCE_MIN } from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';

import { displayMidi, pruneHistory } from '../../../components';

import type { PitchGraphPoint } from '../../../components';

/**
 * livePoints — 네이티브 프레임 스트림을 그래프가 그릴 롤링 창으로 누적하는 순수 변환.
 *
 * 창 길이의 소유자가 여기인 이유: "라이브로 얼마나 되돌아볼 수 있나"는 캡처 쪽 정책이고,
 * 그래프는 받은 것을 그리기만 한다(프롭 구동). 예전엔 그래프가 스스로 네이티브를 구독하며
 * 창까지 들고 있었는데, 그러면 mock 브리지(브라우저 개발 경로)의 프레임이 그래프에
 * 도달하지 못한다 — 브리지는 호출자가 넘긴 콜백으로만 이벤트를 보내기 때문.
 *
 * React·오디오 IO 의존 없음 — vitest 대상.
 */

/**
 * 라이브 그래프가 보여주는 롤링 창(ms).
 *
 * 곡선 진단(음역·노래)은 **테이크 전체**를 화면 폭에 담으므로, 창이 제한시간보다 짧으면
 * 앞부분이 조용히 잘려 나간다 — 저음 진단에서 그건 정확히 재려던 값(어디까지 내려갔나)이
 * 사라지는 것이다. 가장 긴 제한시간(20초)에 여유를 얹었고, 그 관계는
 * `recordTests.spec.ts`가 잡고 있다. 30초 × ~47fps ≈ 1400점이라 비용은 무시할 수준이다.
 */
export const LIVE_WINDOW_MS = 30_000;

/** 이번 테이크에서 부른 음의 폭(표시용 midi). 그래프 세로 창을 잡는 쪽이 쓴다. */
export interface SungRange {
  lo: number;
  hi: number;
}

/**
 * 부른 폭을 **넓히기만** 한다(테이크 안에서 단조). 그래프가 8초 창의 min/max로 세로
 * 축을 잡으면, 고음을 한 번 찍고 내려와 버틴 뒤 그 프레임이 창 밖으로 밀리는 순간
 * 축이 통째로 튄다 — 화면만 봐선 내가 내려간 건지 축이 올라온 건지 알 수 없다.
 * 테이크 전체를 누적하는 이 값은 캡처가 갖는다(창 정책의 소유자가 여기인 것과 같은 이유).
 *
 * 그리지 않는 프레임(무성·저신뢰)은 폭도 못 넓힌다 — 곡선과 축이 같은 근거를 써야
 * 눈금 밖에 아무것도 없는 빈 공간이 생기지 않는다(buildSegments와 같은 신뢰도 선).
 */
export const growSungRange = (
  range: SungRange | null,
  frame: NativeF0Frame,
  smoothingActive: boolean,
): SungRange | null => {
  const midi = displayMidi(frame, smoothingActive);
  if (midi == null || frame.confidence < PITCH_CONFIDENCE_MIN) {
    return range;
  }
  return range
    ? { lo: Math.min(range.lo, midi), hi: Math.max(range.hi, midi) }
    : { lo: midi, hi: midi };
};

/** 프레임 하나를 창에 덧붙이고 창 밖으로 밀려난 것을 버린다. */
export const appendLivePoint = (
  points: PitchGraphPoint[],
  frame: NativeF0Frame,
  smoothingActive: boolean,
  windowMs = LIVE_WINDOW_MS,
): PitchGraphPoint[] =>
  pruneHistory(
    [
      ...points,
      {
        t: frame.timestampMs,
        midi: displayMidi(frame, smoothingActive),
        confidence: frame.confidence,
      },
    ],
    frame.timestampMs,
    windowMs,
  );
